import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import { Icon } from "./Icon";

export interface OpcionTarjeta<T extends string> {
  valor: T;
  titulo: string;
  descripcion?: string;
  /** Glifo de Material Symbols, opcional — no todas las tarjetas lo llevan. */
  icono?: string;
}

export interface TarjetasOpcionProps<T extends string> {
  opciones: OpcionTarjeta<T>[];
  valor: T;
  onCambiar: (valor: T) => void;
  /** 1 columna (apiladas, por defecto), 2 o 3 (lado a lado). La de 3 se
   *  apila a 1 columna por debajo de `sm` — a diferencia de la de 2, que no
   *  lo necesitaba en sus usos actuales, 3 tarjetas angostas en un drawer
   *  mobile (`w-full`) quedan demasiado apretadas. */
  columnas?: 1 | 2 | 3;
  disabled?: boolean;
  className?: string;
}

/**
 * Grupo de tarjetas de opción única del Design System, envolviendo
 * `ui/radio-group`. Un solo componente para dos usos del prototipo: nivel de
 * profundidad (3 tarjetas) y destinatario (4 tarjetas) — mismo patrón visual,
 * distinto contenido.
 */
export function TarjetasOpcion<T extends string>({
  opciones,
  valor,
  onCambiar,
  columnas = 1,
  disabled = false,
  className,
}: TarjetasOpcionProps<T>) {
  return (
    <RadioGroup
      value={valor}
      onValueChange={(v) => onCambiar(v as T)}
      disabled={disabled}
      className={cn(
        "grid gap-1.5",
        columnas === 2 && "grid-cols-2",
        columnas === 3 && "grid-cols-1 sm:grid-cols-3",
        className
      )}
    >
      {opciones.map((op) => {
        const seleccionada = op.valor === valor;
        return (
          <label
            key={op.valor}
            htmlFor={`tarjeta-opcion-${op.valor}`}
            className={cn(
              "flex cursor-pointer items-center gap-2.5 rounded-lg border px-[11px] py-[9px] transition-colors",
              seleccionada
                ? "border-primary-border bg-primary-50"
                : "border-input hover:bg-border-faint/40",
              disabled && "pointer-events-none opacity-50"
            )}
          >
            <RadioGroupItem value={op.valor} id={`tarjeta-opcion-${op.valor}`} />
            {op.icono && (
              <Icon
                name={op.icono}
                size={18}
                className={seleccionada ? "text-primary-hover" : "text-icon-soft"}
              />
            )}
            <div className="min-w-0">
              <div
                className={cn(
                  "text-[12.5px] font-medium",
                  seleccionada && "text-primary-hover"
                )}
              >
                {op.titulo}
              </div>
              {op.descripcion && (
                <div className="text-soft text-[11px]">{op.descripcion}</div>
              )}
            </div>
          </label>
        );
      })}
    </RadioGroup>
  );
}
